import React, { useEffect, useState, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { getOAuthReturnUrl, getAuthorizeUrl } from '../services/oauth2Service';
import { handleOAuth2Callback as handleOAuth2CallbackWithUser } from '../services/authService';

/**
 * OAuth2 回调页面
 * 处理 Hydra 重定向回来的授权码，交换 Token
 */
const OAuth2Callback = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [error, setError] = useState(null);
  const [isProcessing, setIsProcessing] = useState(true);
  const hasProcessed = useRef(false); // 防止 React Strict Mode 重复执行

  useEffect(() => {
    const processCallback = async () => {
      // 防止重复处理（React 18 Strict Mode 会执行两次）
      if (hasProcessed.current) {
        console.log('⏭️ Callback already processed, skipping...');
        return;
      }
      hasProcessed.current = true;
      try {
        // 1. 检查是否有错误参数
        const errorParam = searchParams.get('error');
        if (errorParam) {
          const errorDescription = searchParams.get('error_description') || 'OAuth2 authorization failed';
          throw new Error(`${errorParam}: ${errorDescription}`);
        }

        // 2. 获取授权码和 state
        const code = searchParams.get('code');
        const state = searchParams.get('state');

        if (!code) {
          throw new Error('Missing authorization code');
        }

        if (!state) {
          throw new Error('Missing state parameter');
        }

        console.log('📥 Processing OAuth2 callback...', {
          code: code.substring(0, 20) + '...',
          state: state.substring(0, 10) + '...',
        });

        // 3. 处理回调，交换 Token
        const backendData = await handleOAuth2CallbackWithUser(code, state);

        console.log('✅ OAuth2 login successful!');

        // Check if we are in a popup
        if (window.opener) {
          try {
             console.log('🔄 Sending login success to opener...', {
               targetOrigin: window.location.origin,
               opener: !!window.opener,
               isClosed: window.opener.closed
             });
             
             // 尝试发送消息
             window.opener.postMessage({ 
               type: 'LOGIN_SUCCESS',
               payload: backendData
             }, window.location.origin);
             
             // 尝试关闭窗口
             window.close();
             
             // 如果 window.close() 被阻止（例如非脚本打开的窗口），显示成功提示
             setIsProcessing(false);
             return;
          } catch (e) {
             console.error('⚠️ Failed to post message to opener:', e);
             // Fallback to normal redirect if opener communication fails
          }
        }

        // 4. 获取返回 URL
        const returnUrl = getOAuthReturnUrl();

        console.log('🔄 Redirecting to:', returnUrl);

        // 5. 短暂延迟后跳转（让用户看到成功状态）
        // 使用 window.location.href 而不是 navigate，确保页面完全刷新
        setTimeout(() => {
          window.location.href = returnUrl;
        }, 500);

      } catch (err) {
        console.error('❌ OAuth2 callback failed:', err);
        setError(err.message || 'Authentication failed');
        setIsProcessing(false);

        // 3秒后跳转回 OneAuth 登录页 (Popup)
        setTimeout(async () => {
          try {
             const loginUrl = await getAuthorizeUrl();
             window.location.href = loginUrl;
          } catch (e) {
             console.error('Failed to get authorize url:', e);
             navigate('/login', { replace: true });
          }
        }, 3000);
      }
    };

    processCallback();
  }, [searchParams, navigate]);

  // 成功状态（仅当 window.close() 失败时显示）
  if (!isProcessing && !error && window.opener) {
    return (
      <div style={styles.container}>
        <div style={styles.content}>
          <div style={{...styles.spinner, borderColor: '#52c41a', borderTopColor: 'transparent', animation: 'none', transform: 'none', display: 'flex', alignItems: 'center', justifyContent: 'center'}}>
             <span style={{fontSize: '30px', color: '#52c41a'}}>✓</span>
          </div>
          <h2 style={styles.title}>登录成功</h2>
          <p style={styles.message}>您已成功登录。</p>
          <button
            onClick={() => window.close()}
            style={{
              marginTop: '20px',
              padding: '10px 20px',
              background: '#52c41a',
              color: 'white',
              border: 'none',
              borderRadius: '4px',
              cursor: 'pointer',
              fontSize: '16px'
            }}
          >
            关闭窗口
          </button>
        </div>
      </div>
    );
  }

  // 加载状态
  if (isProcessing && !error) {
    return (
      <div style={styles.container}>
        <div style={styles.content}>
          <div style={styles.spinner}></div>
          <h2 style={styles.title}>正在处理登录...</h2>
          <p style={styles.message}>请稍候，正在完成登录。</p>
        </div>
      </div>
    );
  }

  // 错误状态
  if (error) {
    return (
      <div style={styles.container}>
        <div style={styles.content}>
          <div style={styles.errorIcon}>✖</div>
          <h2 style={styles.title}>登录失败</h2>
          <p style={styles.errorMessage}>{error}</p>
          <p style={styles.message}>正在跳转到登录页面...</p>
        </div>
      </div>
    );
  }

  return null;
};

// 简单的内联样式（可以根据项目实际情况调整）
const styles = {
  container: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    minHeight: '100vh',
    backgroundColor: '#f5f5f5',
  },
  content: {
    textAlign: 'center',
    padding: '40px',
    backgroundColor: 'white',
    borderRadius: '8px',
    boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
    maxWidth: '400px',
  },
  spinner: {
    border: '4px solid #f3f3f3',
    borderTop: '4px solid #3498db',
    borderRadius: '50%',
    width: '50px',
    height: '50px',
    animation: 'spin 1s linear infinite',
    margin: '0 auto 20px',
  },
  title: {
    fontSize: '24px',
    fontWeight: 'bold',
    marginBottom: '10px',
    color: '#333',
  },
  message: {
    fontSize: '14px',
    color: '#666',
    marginTop: '10px',
  },
  errorIcon: {
    fontSize: '50px',
    color: '#e74c3c',
    marginBottom: '20px',
  },
  errorMessage: {
    fontSize: '14px',
    color: '#e74c3c',
    marginTop: '10px',
    fontWeight: '500',
  },
};

// CSS 动画（需要添加到全局样式或使用 styled-components）
const styleSheet = document.createElement('style');
styleSheet.textContent = `
  @keyframes spin {
    0% { transform: rotate(0deg); }
    100% { transform: rotate(360deg); }
  }
`;
document.head.appendChild(styleSheet);

export default OAuth2Callback;
